import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import prettierBytes from 'prettier-bytes';
import Modal from '@medflex/components/stories/Modal';
import Button from '@medflex/components/stories/Button';
import { IconUpload, IconClose } from '@medflex/components/stories/Icons';
import { FileInput } from '@medflex/components/stories/FileUpload';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import useUppy from '@medflex/hooks/useUppy';
import useUserSession from '@medflex/hooks/useUserSession';
import RenderFileIconModule from '@medflex/components/stories/RenderFileIconModule';
import config from '@medflex/utils/config';
import { ALLOWED_INVITATION_FILE_MIME_TYPES } from '@medflex/utils/constants';
import useModal from '../hooks/useModal';
import useUnloadWarning from '../hooks/useUnloadWarning';
import useMessages from '../hooks/useMessages';

const SUPPORT_PATIENT_ID = 'pat-00000000000000000000000000000000';

const FileItem = ({ mimeType, name, bytes, extension, onDelete }) => (
  <div className="relative py-6 pl-4 pr-8 bg-blue-100 rounded-lg inline-flex lg:max-w-three-quarters">
    <div className="text-green-500">
      <RenderFileIconModule mime={mimeType} />
    </div>
    <div className="flex flex-col justify-center pl-4 min-w-0">
      <div className="font-semibold truncate">{name}</div>
      <p className="text-slate-500">
        <span>{prettierBytes(bytes)}</span>
        <span className="px-1">/</span>
        <span>{extension}</span>
      </p>
    </div>
    <Button
      size="xs"
      shape="circle"
      onClick={onDelete}
      title="Datei entfernen"
      className="ml-2 top-0 right-0 absolute"
      kind="secondary:ghost"
    >
      <IconClose size="text" />
    </Button>
  </div>
);

FileItem.propTypes = {
  mimeType: PropTypes.string,
  name: PropTypes.string,
  bytes: PropTypes.number,
  extension: PropTypes.string,
  onDelete: PropTypes.func,
};

const MultiplePatientInvitationModule = () => {
  const { send: sendMessage } = useMessages();
  const { t } = useTranslation('physician');
  const { session } = useUserSession();
  const superuser = useSelector(({ superuser: user }) => user);
  const dropTarget = useRef();
  const formRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [retried, setRetried] = useState(false);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const {
    files,
    register: registerFileInput,
    uploading,
    upload,
    reset,
    errors: fileErrors,
    retry,
  } = useUppy({
    dropTarget,
    endpoint: `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/uploads`,
    // @todo: enable multiple uploads for contactChat once endpoint supports it
    multiple: false,
    headers: {
      AUTHORIZATION: `Bearer ${session.access_token || ''}`,
      'Upload-Metadata2': btoa(
        JSON.stringify({
          sender: superuser.id,
          recipient: SUPPORT_PATIENT_ID,
        }),
      ),
    },
  });
  const acceptedFiles = ALLOWED_INVITATION_FILE_MIME_TYPES.join(',');
  const fileInputProps = { ...registerFileInput(), accept: acceptedFiles };
  useUnloadWarning(uploading);

  const { close } = useModal();

  const cancel = () => {
    reset();
    trackEvent({
      zone: 'multiPatientInvitation',
      actionText: TRACKING_ACTIONS.CLOSE,
      elementType: TRACKING_ELEMENT_TYPES.FORM,
    });
    close({ modal: 'multiPatientInvitation' });
  };

  const retryFailedFiles = useCallback(
    () => Promise.all(fileErrors.map(error => retry(error.file))),
    [fileErrors],
  );

  const send = async () => {
    try {
      const getFileIdFromUri = uri => uri.split('/').reverse()[0];
      const formData = new FormData(formRef.current);
      const message = formData.get('message');
      await sendMessage({
        recipient: SUPPORT_PATIENT_ID,
        text: message,
        ...(files && { files: [getFileIdFromUri(files[0].uploadURL)] }),
      });
      close({ modal: 'multiPatientInvitation' });
      showSuccessToast(
        t('multiple_patient_invitation.successMessage.title'),
        t('multiple_patient_invitation.successMessage.description'),
        { toastId: 'multiPatientInviteSuccess' },
      );
    } catch {
      showAlertToast(
        t('multiple_patient_invitation.errorMessage.title'),
        t('multiple_patient_invitation.errorMessage.description'),
        { toastId: 'multiPatientInviteFailed' },
      );
      setSubmitting(false);
    }
  };

  const fileUploadCompleted = useMemo(() => {
    if (!uploading && files.length > 0) {
      if (!files.some(({ progress }) => !progress.uploadComplete)) {
        return true;
      }
    }
    return false;
  }, [files, uploading]);

  useEffect(() => {
    if (fileUploadCompleted) send();
  }, [fileUploadCompleted]);

  useEffect(() => {
    if (fileErrors.length && retried) {
      if (submitting) {
        showAlertToast(
          t('multiple_patient_invitation.errorMessage.title'),
          t('multiple_patient_invitation.errorMessage.description'),
          { toastId: 'multiPatientInviteFailed' },
        );
        setSubmitting(false);
      }
    }
  }, [fileErrors, retried]);

  const sendMultiPatientInvitation = async () => {
    setRetried(false);
    setSubmitting(true);
    trackEvent({
      zone: 'multiPatientInvitation',
      actionText: TRACKING_ACTIONS.SUBMIT,
      elementType: TRACKING_ELEMENT_TYPES.FORM,
    });
    if (fileErrors.length > 0) {
      await retryFailedFiles();
      setRetried(true);
      return;
    }
    upload();
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const handleSubmit = event => {
    event.preventDefault();
    setDisplayConfirmationModal(true);
  };

  const handleSelect = (event, select) => {
    select();
    trackEvent(event, {
      zone: 'multiPatientInvitation',
    });
  };

  return (
    <Modal
      showCloseButton
      onClickCloseButton={cancel}
      onClickOutside={cancel}
      title={t('multiple_patient_invitation.title')}
    >
      <div
        ref={dropTarget}
        data-component="MultiplePatientInvitationModule"
        className="flex flex-col"
      >
        <p className="mt-2">{t('multiple_patient_invitation.subtitle')}</p>
        <div className="flex justify-center mt-4 font-semibold">
          <div className="px-6 py-1 bg-blue-100 border border-slate-300">
            {t('multiple_patient_invitation.firstName')}
          </div>
          <div className="px-6 py-1 bg-blue-100 border border-slate-300 border-l-0">
            {t('multiple_patient_invitation.lastName')}
          </div>
          <div className="px-6 py-1 bg-blue-100 border border-slate-300 border-l-0">
            {t('multiple_patient_invitation.emailMobile')}
          </div>
        </div>
        <p className="mt-4">{t('multiple_patient_invitation.requirements')}</p>
        <form ref={formRef} className="flex flex-col" onSubmit={handleSubmit}>
          <input type="hidden" name="message" value={t('multiple_patient_invitation.message')} />
          <div className="flex items-center mt-4">
            {files.length > 0 ? (
              <ul className="flex-auto">
                {files.map(({ name, id, type, size, extension }) => (
                  <li key={id} className="flex items-center">
                    <FileItem
                      name={name}
                      mimeType={type}
                      bytes={size}
                      extension={extension}
                      onDelete={reset}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <FileInput {...fileInputProps}>
                {({ select }) => (
                  <Button
                    className="self-start flex-shrink-0"
                    kind="primary:outline"
                    onClick={event => handleSelect(event, select)}
                    isDisabled={files.length > 0}
                  >
                    <IconUpload size="5" className="mr-3" />
                    {t('multiple_patient_invitation.chooseFile')}
                  </Button>
                )}
              </FileInput>
            )}
          </div>
          <p className="mt-4">{t('multiple_patient_invitation.info')}</p>
          <Button
            type="submit"
            className="self-center my-16"
            isDisabled={submitting || !files.length}
          >
            {t('multiple_patient_invitation.submit')}
          </Button>
        </form>
        {displayConfirmationModal ? (
          <Modal
            title={t('multiple_patient_invitation.dialog.title')}
            showCloseButton
            onClickCloseButton={hideConfirmationModal}
          >
            <div className="mb-6">
              {t('multiple_patient_invitation.dialog.text', { fileName: files[0].name })}
            </div>
            <footer className="flex justify-center">
              <Button onClick={sendMultiPatientInvitation}>
                {t('multiple_patient_invitation.dialog.confirm')}
              </Button>
            </footer>
          </Modal>
        ) : null}
      </div>
    </Modal>
  );
};

export default MultiplePatientInvitationModule;
